import React, { useEffect, useState } from 'react';

export default function DocumentsAdmin() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [file, setFile] = useState(null);
  const [subdir, setSubdir] = useState('agm');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [year, setYear] = useState('');

  const load = () => {
    let m = true;
    setLoading(true);
    fetch('/api/documents')
      .then((r) => r.ok ? r.json() : Promise.reject('Failed to load'))
      .then((data) => { if (m) setDocs(Array.isArray(data) ? data : []); })
      .catch(() => setDocs([]))
      .finally(() => m && setLoading(false));
    return () => { m = false; };
  };

  useEffect(() => { load(); }, []);

  const uploadFile = () =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = async () => {
        try {
          const base64 = reader.result.split(',')[1];
          const path = `documents/${subdir}/${file.name}`;
          const res = await fetch('/api/documents/upload', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ path, contentBase64: base64, contentType: file.type }),
          });
          if (!res.ok) throw new Error(await res.text());
          resolve((await res.json()).url);
        } catch (e) { reject(e); }
      };
      reader.readAsDataURL(file);
    });

  const handleAdd = async () => {
    setError('');
    setMessage('');
    setSaving(true);
    try {
      let fileUrl = '';
      if (file) fileUrl = await uploadFile();

      const entry = {
        title: title || file?.name || '',
        year: year || new Date().getFullYear(),
        category: category || '',
        fileUrl,
        published: new Date().toISOString().slice(0, 10),
      };

      const newDocs = [...docs, entry];
      const res = await fetch('/api/documents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newDocs),
      });
      if (!res.ok) throw new Error(await res.text());
      setDocs(await res.json());
      setFile(null);
      setTitle('');
      setCategory('');
      setYear('');
      setMessage('Document added.');
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (idx) => {
    if (!window.confirm('Remove this document?')) return;
    setSaving(true);
    setError('');
    try {
      const newDocs = docs.filter((_, i) => i !== idx);
      const res = await fetch('/api/documents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newDocs),
      });
      if (!res.ok) throw new Error(await res.text());
      setDocs(await res.json());
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="admin-header">
        <div>
          <h2>Documents</h2>
          <p>Upload and manage documents in the public Document Library.</p>
        </div>
      </div>

      {error && <div className="admin-msg-error">{error}</div>}
      {message && <div className="admin-msg-success">{message}</div>}

      <div className="admin-form">
        <h3>Add document</h3>
        <div className="admin-form-grid">
          <label>
            <span>Title</span>
            <input placeholder="Document title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label>
            <span>Category</span>
            <input placeholder="e.g. Minutes, Newsletter" value={category} onChange={(e) => setCategory(e.target.value)} />
          </label>
          <label>
            <span>Year</span>
            <input placeholder={new Date().getFullYear()} value={year} onChange={(e) => setYear(e.target.value)} />
          </label>
          <label>
            <span>Subfolder</span>
            <input placeholder="e.g. agm" value={subdir} onChange={(e) => setSubdir(e.target.value)} />
          </label>
          <label className="full-width">
            <span>File</span>
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
        </div>
        <div className="admin-form-actions">
          <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={saving || !file}>
            {saving ? 'Uploading…' : 'Upload & Add'}
          </button>
        </div>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : (
        <div className="admin-list">
          {docs.length === 0
            ? <p style={{ color: 'var(--text-muted)' }}>No documents yet.</p>
            : docs.map((d, i) => (
                <div className="admin-item" key={`${d.title}-${i}`}>
                  <div className="admin-item-info">
                    <div className="admin-item-title">{d.title}</div>
                    <div className="admin-item-meta">
                      {d.category && <span className="badge">{d.category}</span>}
                      {d.year && <span>{d.year}</span>}
                      {d.published && <span>Published {d.published}</span>}
                    </div>
                    <div className="admin-item-body" style={{ wordBreak: 'break-all' }}>{d.fileUrl}</div>
                  </div>
                  <div className="admin-item-btns">
                    <button className="btn btn-outline btn-sm" onClick={() => handleRemove(i)} disabled={saving}>Remove</button>
                  </div>
                </div>
              ))
          }
        </div>
      )}
    </div>
  );
}
